import {
  IStorey,
  ProjectMetadata,
  FootprintMetadata,
} from '@shared/models/project.model';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import {
  createBuildingDesign,
  createActivityReference,
  createStorey,
  createShape,
  createActivity,
} from '../factories';
import {
  Activity,
  ActivityPropertyMap,
} from '@shared/models/design/activities/activity.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import {
  Activity as DeprecatedActivity,
  ActivityEnum,
  FutureActivities,
} from '@shared/models/activities.model';
import { Coordinate } from '@shared/models/geography.model';
import {
  clamp,
  getMean,
  isNumeric,
  roundToDecimals,
  sum,
} from '@shared/helpers/math.helpers';
import { ActivityId } from '@shared/models/activity-class.model';
import {
  createActivityFromActivityId,
  migrateStringGfa,
} from './legacy-create-activity-from-id.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  convertCoordinateToMetricOffset,
  getDistance,
} from '@shared/helpers/geometry/coordinates.helpers';
import {
  computePolygonArea,
  isPointInPolygon,
  isPolygon2D,
  multiplyPoint2D,
} from '@shared/helpers/geometry/geometry.helpers';
import { isPolygonSelfIntersecting } from '@shared/helpers/geometry/polygon.helpers';
import {
  Shape,
  Storey,
  STOREY_MIN_HEIGHT,
} from '@shared/models/design/storey.model';
import { Point2D } from '@shared/models/geometry.model';
import { cloneDeep } from 'lodash';
import {
  COORDINATE_STOCKHOLM,
  expressionVariablesConstants,
} from '@shared/constants';
import { throwIfArrayContainsError, throwIfError } from '../../utils.helpers';
import { OptionalReadonly } from '@shared/models/type-helpers.model';
import type { PartialRecord } from '@shared/models/type-helpers.model';
import { activityRefGuards } from '../guards/activity-guards.helpers';
import { validateBuildingDesign } from '../validation/design.validation';
import { clampMezzanineStoreysInStorey } from '../mezzanine-helpers';
import { scaleBuildingToGfa } from '../scale-building-gfa.helpers';

const SHAPE_SCALE_TOLERANCE = 0.001;
const DEFAULT_APARTMENTS_RATIO = 0.85;
const DEFAULT_POINT: Point2D = [0, 0];
const DEFAULT_STOREY_HEIGHT = 3;
/** Legacy metadata default clear height (m) when inner_height was omitted. */
const OLD_DEFAULT_INNER_HEIGHT = 2.7;
const FOOTPRINT_DEDUPE_DISTANCE = 0.1;
const RECTANGLE_DOT_TOLERANCE = 0.1;
const RECTANGLE_PARALLEL_TOLERANCE = 0.1;
const RECTANGLE_LENGTH_TOLERANCE = 0.05;
const RATIO_PRIMARY_ACTIVITY_TYPES = new Set<ActivityType>([
  ActivityType.Residential,
  ActivityType.Office,
  ActivityType.Industry,
]);
const roundGfa = (value: number): number => roundToDecimals(value, 8);

export type LegacyProjectMetadata = ProjectMetadata & {
  building_lifetime?: number;
  below_ground?: number;
  storeys?: IStorey[];
  building_footprint?: FootprintMetadata;
  gfa_building?: number;
  building_perimeter?: number;
  activity_id?: ActivityId;
  activities?: DeprecatedActivity[];
  la_per_apartment?: number | string | null;
  apartments_per_stairwell_per_level?: number | string | null;
  elevators_per_stairwell?: number | string | null;
};

/**
 * Get legacy gross floor area from metadata stored before design became source of truth.
 * @param meta Legacy project metadata.
 * @returns Gross floor area.
 */
export const getLegacyBuildingGFA = (meta: LegacyProjectMetadata): number => {
  if (meta.gfa_building) return Math.max(1, meta.gfa_building);
  // Guard against legacy projects persisted without a footprint.
  const footprintArea = meta.building_footprint?.area ?? 0;
  return Math.max(1, footprintArea * Math.max(1, meta.storeys?.length ?? 1));
};

/**
 * Best-effort conversion of legacy activity values.
 * Legacy activity properties have historically been persisted as number|string|null.
 */
const toOptionalNumber = (value: unknown): number | undefined => {
  if (!isNumeric(value)) return;
  return guards.is.number(value) ? value : +String(value);
};

/**
 * Check if a number is positive and finite.
 * @param value Candidate number.
 * @returns True when the value is a positive number.
 */
const isPositiveNumber = (value: number | undefined): value is number => {
  if (value === undefined) return false;
  if (!isNumeric(value)) return false;
  return value > 0;
};

/**
 * Get legacy apartment overrides from project metadata.
 */
const getLegacyResidentialPropertyOverrides = (
  meta: LegacyProjectMetadata,
): Partial<ActivityPropertyMap[ActivityType.Residential]> => {
  const livingArea = toOptionalNumber(meta.la_per_apartment);
  if (!isPositiveNumber(livingArea)) return {};
  return { living_area_per_apartment: livingArea };
};

/**
 * Get legacy stairwell overrides from project metadata.
 */
const getLegacyStairwellsPropertyOverrides = (
  meta: LegacyProjectMetadata,
): Partial<ActivityPropertyMap[ActivityType.Stairwells]> => {
  const apartmentsPerStairwell = toOptionalNumber(
    meta.apartments_per_stairwell_per_level,
  );
  const elevatorsPerStairwell = toOptionalNumber(meta.elevators_per_stairwell);
  const overrides: Partial<ActivityPropertyMap[ActivityType.Stairwells]> = {};
  if (isPositiveNumber(apartmentsPerStairwell)) {
    overrides.apartments_per_stairwell_per_storey = apartmentsPerStairwell;
  }
  if (isPositiveNumber(elevatorsPerStairwell)) {
    overrides.elevators_per_stairwell = elevatorsPerStairwell;
  }
  return overrides;
};

/**
 * Detect the legacy industry + two storeys mezzanine migration case.
 * @param meta Project metadata.
 * @returns True when the mezzanine migration rules should apply.
 */
const isMezzanineMigration = (meta: LegacyProjectMetadata): boolean =>
  meta.activity_id === ActivityId.Industry && (meta.storeys?.length ?? 0) === 2;

/**
 * Get storey GFA from legacy metadata.
 * @param storey Legacy storey.
 * @returns Numeric GFA or undefined.
 */
const getStoreyGfa = (storey?: IStorey): number | undefined =>
  toOptionalNumber(storey?.gfa);

/**
 * Resolve target GFA values for mezzanine migration.
 * @param meta Project metadata.
 * @returns Base and mezzanine target GFA.
 */
const getMezzanineMigrationGfa = (
  meta: LegacyProjectMetadata,
): { baseTarget: number | undefined; mezzanineTarget: number | undefined } => {
  if (!isMezzanineMigration(meta)) {
    return { baseTarget: undefined, mezzanineTarget: undefined };
  }
  const storeys = meta.storeys ?? [];
  const baseGfa = getStoreyGfa(storeys[0]);
  const mezzanineGfa = getStoreyGfa(storeys[1]);
  const totalGfa = getLegacyBuildingGFA(meta);

  const resolvedMezzanine = isPositiveNumber(mezzanineGfa)
    ? mezzanineGfa
    : undefined;
  const resolvedBase = isPositiveNumber(baseGfa)
    ? baseGfa
    : isPositiveNumber(resolvedMezzanine) && isPositiveNumber(totalGfa)
      ? totalGfa - resolvedMezzanine
      : undefined;

  return {
    baseTarget: isPositiveNumber(resolvedBase) ? resolvedBase : undefined,
    mezzanineTarget: resolvedMezzanine,
  };
};

/**
 * Resolve a fallback storey height when legacy data is missing.
 * @param storey Legacy storey.
 * @returns Height to use for storey calculations.
 */
const getStoreyHeightFallback = (storey?: IStorey): number => {
  const height = toOptionalNumber(storey?.inner_height);
  if (isPositiveNumber(height)) return height;
  // Fall back to the default storey height when legacy data is missing.
  return DEFAULT_STOREY_HEIGHT;
};

/**
 * Resolve the inward normal for a shape edge by probing inside the polygon.
 * @param points Shape points.
 * @param edgeStartIndex Edge start index.
 * @returns Inward normal or undefined when invalid.
 */
const getInwardNormalForEdge = (
  points: ReadonlyArray<Point2D>,
  edgeStartIndex: number,
): Point2D | undefined => {
  if (points.length < 3) return undefined;
  const start = points[edgeStartIndex] ?? DEFAULT_POINT;
  const end = points[(edgeStartIndex + 1) % points.length] ?? DEFAULT_POINT;
  const [dx, dy] = [end[0] - start[0], end[1] - start[1]];
  const length = Math.hypot(dx, dy);
  if (!Number.isFinite(length) || length <= 0) return undefined;

  const candidateA: Point2D = [dy / length, -dx / length];
  const candidateB: Point2D = [-dy / length, dx / length];
  const midpoint: Point2D = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2];
  const probeDistance = 0.01;

  // Pick the normal that points inside the polygon to keep shapes contained.
  const probeA: Point2D = [
    midpoint[0] + candidateA[0] * probeDistance,
    midpoint[1] + candidateA[1] * probeDistance,
  ];
  if (isPointInPolygon(probeA, points)) return candidateA;

  const probeB: Point2D = [
    midpoint[0] + candidateB[0] * probeDistance,
    midpoint[1] + candidateB[1] * probeDistance,
  ];
  if (isPointInPolygon(probeB, points)) return candidateB;

  return candidateA;
};

/**
 * Build the mezzanine storeys for the legacy industry migration case.
 * @param params Migration parameters.
 * @returns Mezzanine storeys or undefined when validation fails.
 */
const buildMezzanineMigrationStoreys = (params: {
  baseInnerHeight: number;
  mezzanineInnerHeight: number;
  storeys: NonNullable<LegacyProjectMetadata['storeys']>;
  baseShape: Shape;
  groundFloorElevation: number;
  storeyActivityRefs: ReturnType<typeof createActivityReference>[];
  gfa: number;
  storeyCount: number;
  mezzanineTargetGfa?: number;
}): Storey[] => {
  const {
    baseInnerHeight: baseInnerHeight,
    mezzanineInnerHeight: mezzanineInnerHeight,
    storeys,
    baseShape,
    groundFloorElevation,
    storeyActivityRefs,
    gfa,
    storeyCount,
    mezzanineTargetGfa,
  } = params;
  const [baseStorey, mezzanineStorey] = storeys;
  const baseHeight = heightFromInnerHeight(baseInnerHeight);
  if (!baseStorey || !mezzanineStorey || baseHeight < STOREY_MIN_HEIGHT)
    return [];
  const mezzanineHeight = clamp(
    heightFromInnerHeight(mezzanineInnerHeight),
    STOREY_MIN_HEIGHT,
    baseHeight - STOREY_MIN_HEIGHT,
  );
  const mezzanineElevation = clamp(
    baseHeight - mezzanineHeight,
    0,
    baseHeight - STOREY_MIN_HEIGHT,
  );

  const mezzanineGfa =
    mezzanineTargetGfa ?? getStoreyGfa(mezzanineStorey) ?? gfa / storeyCount;
  // Move a single edge to hit mezzanine GFA while keeping edges aligned.
  const mezzanineShape = scaleShapeToTargetAreaByMovingEdge(
    cloneDeep(baseShape),
    mezzanineGfa,
  );

  const storeysResult = [
    createStorey({
      name: baseStorey?.name,
      height: baseHeight,
      elevation: groundFloorElevation,
      shapes: [cloneDeep(baseShape)],
      activities: storeyActivityRefs,
    }),
    createStorey({
      name: mezzanineStorey?.name,
      height: mezzanineHeight,
      mezzanine: true,
      mezzanine_elevation: mezzanineElevation,
      shapes: [mezzanineShape],
      activities: storeyActivityRefs,
    }),
  ];

  // Clamp only when needed to avoid unnecessary geometry drift.
  const parentStorey = storeysResult[0];
  const mezzanine = storeysResult[1];
  if (!parentStorey || !mezzanine?.shapes?.length) return storeysResult;
  const needsClamp = mezzanine.shapes.some(
    (shape) =>
      isPolygonSelfIntersecting(shape.points) ||
      !isShapeInsideParentStorey(shape, parentStorey),
  );
  if (!needsClamp) return storeysResult;

  const clamped = clampMezzanineStoreysInStorey(storeysResult, parentStorey);
  // Edge-move + clamp can still produce self-intersecting shapes; fall back to uniform scaling.
  const clampedMezzanine = clamped[1];
  const stillBroken = clampedMezzanine?.shapes.some((s) =>
    isPolygonSelfIntersecting(s.points),
  );
  if (!stillBroken) return clamped;

  const fallbackShape = scaleShapeToTargetArea(
    cloneDeep(baseShape),
    mezzanineGfa,
  );
  return [
    storeysResult[0],
    createStorey({
      ...storeysResult[1],
      shapes: [fallbackShape],
    }),
  ].filter(guards.is.defined);
};

/**
 * Convert legacy {@link DeprecatedActivity} fields into design {@link Activity.properties}.
 */
const migrateDeprecatedActivityProperties = (
  deprecated: DeprecatedActivity,
): PartialRecord<string, number> => {
  const properties: PartialRecord<string, number> = {};

  if (deprecated.type === ActivityEnum.Apartments) {
    const livingAreaPerApartment =
      'livingAreaPerApartment' in deprecated
        ? toOptionalNumber(deprecated.livingAreaPerApartment)
        : undefined;
    const balconyAreaPerApartment =
      'balconyAreaPerApartment' in deprecated
        ? toOptionalNumber(deprecated.balconyAreaPerApartment)
        : undefined;
    const bathroomAreaPerApartment =
      'bathroomAreaPerApartment' in deprecated
        ? toOptionalNumber(deprecated.bathroomAreaPerApartment)
        : undefined;

    if (livingAreaPerApartment !== undefined) {
      properties.living_area_per_apartment = livingAreaPerApartment;
    }
    if (balconyAreaPerApartment !== undefined) {
      properties.balcony_area_per_apartment = balconyAreaPerApartment;
    }
    if (bathroomAreaPerApartment !== undefined) {
      properties.bathroom_area_per_apartment = bathroomAreaPerApartment;
    }
  } else if (deprecated.type === ActivityEnum.Stairwells) {
    const apartmentsPerStairwellPerStorey =
      'apartmentsPerStairwellPerStorey' in deprecated
        ? toOptionalNumber(deprecated.apartmentsPerStairwellPerStorey)
        : undefined;
    const elevatorsPerStairwell =
      'elevatorsPerStairwell' in deprecated
        ? toOptionalNumber(deprecated.elevatorsPerStairwell)
        : undefined;

    if (apartmentsPerStairwellPerStorey !== undefined) {
      properties.apartments_per_stairwell_per_storey =
        apartmentsPerStairwellPerStorey;
    }
    if (elevatorsPerStairwell !== undefined) {
      properties.elevators_per_stairwell = elevatorsPerStairwell;
    }
  }

  return properties;
};

export const migrateProjectMetadataToBuildingDesign = (
  meta: LegacyProjectMetadata,
): BuildingDesign => {
  // Return new default geometry if no metadata is found
  if (!meta) return createBuildingDesign();

  const gfa = getLegacyBuildingGFA(meta);
  const storeyCount = Math.max(1, meta.storeys?.length ?? 0);
  // Carry the legacy below-ground depth into the ground-floor elevation.
  const groundFloorElevation = getGroundFloorElevation(meta.below_ground);

  // Build activities from legacy metadata using the same fallback logic as energy
  const activitiesAndGfa = getActivitiesWithGfaFromMeta(meta);
  const totalArea = sum(activitiesAndGfa, 'gfa');

  const originalStoreys = meta.storeys?.length ? meta.storeys : undefined;
  const mezzanineMigration = isMezzanineMigration(meta);
  const mezzanineMigrationGfa = mezzanineMigration
    ? getMezzanineMigrationGfa(meta)
    : { baseTarget: undefined, mezzanineTarget: undefined };
  const primaryStoreyGfa = mezzanineMigration
    ? mezzanineMigrationGfa.baseTarget
    : undefined;
  const baseHeightValue = mezzanineMigration
    ? getStoreyHeightFallback(originalStoreys?.[0])
    : toOptionalNumber(originalStoreys?.[0]?.inner_height);
  const mezzanineHeightValue = mezzanineMigration
    ? getStoreyHeightFallback(originalStoreys?.[1])
    : toOptionalNumber(originalStoreys?.[1]?.inner_height);
  const canMigrateMezzanine =
    mezzanineMigration &&
    originalStoreys?.length === 2 &&
    isPositiveNumber(baseHeightValue) &&
    isPositiveNumber(mezzanineHeightValue);

  const coords = [...(meta.building_footprint?.coordinates ?? [])];
  const shape = scaleShapeToTargetArea(
    getShapeFromCoordinates(coords),
    primaryStoreyGfa ?? gfa / storeyCount,
  );
  // Normalize after scaling; resolve when self-intersecting so validation passes.
  let points = normalizeFootprintPoints([...shape.points]);
  if (points.length > 3 && isPolygonSelfIntersecting(points)) {
    points = resolveSelfIntersectingFootprint(points);
  }

  const finalShape = createShape({ points });

  const storeyActivityRefs = activitiesAndGfa.map(({ activity, gfa }) =>
    createActivityReference(activity.id, activity.type, gfa / totalArea),
  );

  const storeys = throwIfArrayContainsError(
    canMigrateMezzanine
      ? buildMezzanineMigrationStoreys({
          baseInnerHeight: baseHeightValue,
          mezzanineInnerHeight: mezzanineHeightValue,
          storeys: originalStoreys,
          baseShape: finalShape,
          groundFloorElevation,
          storeyActivityRefs,
          gfa,
          storeyCount,
          mezzanineTargetGfa: mezzanineMigrationGfa.mezzanineTarget,
        })
      : (originalStoreys?.map((s, index) =>
          createStorey({
            name: s?.name,
            height: heightFromInnerHeight(toOptionalNumber(s.inner_height)),
            elevation: index === 0 ? groundFloorElevation : undefined,
            shapes: [cloneDeep(finalShape)],
            activities: storeyActivityRefs,
          }),
        ) ?? [
          // If no storeys, create a single storey
          createStorey({
            elevation: groundFloorElevation,
            shapes: [cloneDeep(finalShape)],
            height: heightFromInnerHeight(),
            activities: storeyActivityRefs,
          }),
        ]),
  );

  // Note: location on Project is a number, not a Coordinate. Use defaults.
  const buildingDesign = createBuildingDesign({
    activities: activitiesAndGfa.map((a) => a.activity),
    storeys: throwIfArrayContainsError(storeys),
    location: getLocationFromCoordinates(
      meta.building_footprint?.coordinates ?? [COORDINATE_STOCKHOLM],
    ),
  });

  const validatedDesign = throwIfError(validateBuildingDesign(buildingDesign));
  // Mezzanine storey doesn't get a perfectly accurate area, so we need to scale the entire building to the target GFA.
  return scaleBuildingToGfa(validatedDesign, gfa);
};

const heightFromInnerHeight = (
  innerHeight: number = OLD_DEFAULT_INNER_HEIGHT,
): number => {
  return Math.max(
    1,
    innerHeight + expressionVariablesConstants.floor_slab_height,
  );
};

/**
 * Convert building footprint coordinates to a shape with metric offset
 * @param coordinates
 * @returns
 */
export const getShapeFromCoordinates = (
  coordinates: OptionalReadonly<Coordinate[]>,
): Shape => {
  const center = getLocationFromCoordinates(coordinates);
  let points = normalizeFootprintPoints(
    coordinates.map((coordinate) =>
      convertCoordinateToMetricOffset(coordinate, center),
    ),
  );
  // Fallback: ensure we never return a self-intersecting polygon (e.g. missed by epsilon or after scaling).
  if (points.length > 3 && isPolygonSelfIntersecting(points)) {
    points = resolveSelfIntersectingFootprint(points);
  }

  return createShape({ points });
};

/**
 * Normalize footprint points to avoid invalid polygons.
 * @param points Raw footprint points.
 * @returns Sanitized points.
 */
export const normalizeFootprintPoints = (points: Point2D[]): Point2D[] => {
  const deduped = removeNearbyFootprintPoints(
    points,
    FOOTPRINT_DEDUPE_DISTANCE,
  );
  if (!isPolygon2D(deduped)) return deduped;
  if (!isPolygonSelfIntersecting(deduped)) return deduped;

  return resolveSelfIntersectingFootprint(deduped);
};

/**
 * Remove consecutive points that are nearly overlapping.
 * @param points Footprint points.
 * @param tolerance Distance threshold.
 * @returns Cleaned points.
 */
const removeNearbyFootprintPoints = (
  points: Point2D[],
  tolerance: number,
): Point2D[] => {
  if (points.length <= 1) return points;
  const deduped = points.reduce<Point2D[]>((acc, point) => {
    const prev = acc[acc.length - 1];
    if (prev && getDistance(prev, point) <= tolerance) return acc;
    acc.push(point);
    return acc;
  }, []);
  if (deduped.length < 2) return deduped;
  const first = deduped[0] ?? DEFAULT_POINT;
  const last = deduped[deduped.length - 1] ?? DEFAULT_POINT;
  if (getDistance(first, last) <= tolerance) {
    return deduped.slice(0, -1);
  }
  return deduped;
};

/**
 * Resolve self-intersecting footprints without reordering points.
 * Removes the point on the shortest edge repeatedly until the polygon has at most 3 points or is no longer self-intersecting.
 * @param points Footprint points.
 * @param options Optional. `epsilon`: tolerance for segment intersection (default from isPolygonSelfIntersecting).
 * @returns Simple polygon points.
 */
export const resolveSelfIntersectingFootprint = (
  points: Point2D[],
): Point2D[] => {
  let current = [...points];
  const maxIterations = Math.max(points.length * 2, 20);
  let iterations = 0;

  while (
    current.length > 3 &&
    isPolygonSelfIntersecting(current) &&
    iterations < maxIterations
  ) {
    const indexToRemove = getShortestEdgePointIndex(current);
    if (indexToRemove < 0) break;
    // Remove the point on the shortest local edge to reduce spikes.
    current = current.filter((_, index) => index !== indexToRemove);
    iterations += 1;
  }

  return current;
};

/**
 * Check if a shape is fully inside a parent storey shape.
 * @param shape Child shape.
 * @param parentStorey Parent storey.
 * @returns True when the shape is contained.
 */
const isShapeInsideParentStorey = (
  shape: Shape,
  parentStorey: Storey,
): boolean => {
  const parentShape = parentStorey.shapes?.[0];
  if (!parentShape || !isPolygon2D(parentShape.points)) return false;
  return shape.points.every((point) =>
    isPointInPolygon(point, parentShape.points),
  );
};

/**
 * Pick the point with the shortest local edge.
 * @param points Footprint points.
 * @returns Index of point to remove or -1 when unavailable.
 */
const getShortestEdgePointIndex = (points: Point2D[]): number => {
  if (points.length < 4) return -1;
  let shortest = Number.POSITIVE_INFINITY;
  let indexToRemove = -1;

  for (let i = 0; i < points.length; i += 1) {
    const prev = points[(i - 1 + points.length) % points.length];
    const next = points[(i + 1) % points.length];
    const current = points[i];
    if (!prev || !next || !current) continue;
    const edgeLength = Math.min(
      getDistance(prev, current),
      getDistance(current, next),
    );
    if (edgeLength >= shortest) continue;
    shortest = edgeLength;
    indexToRemove = i;
  }

  return indexToRemove;
};

/**
 * Scale a shape so its area matches the provided target area.
 * @param shape
 * @param targetArea
 */
export const scaleShapeToTargetArea = (
  shape: Shape,
  targetArea: number,
): Shape => {
  if (!Number.isFinite(targetArea) || targetArea <= 0) return shape;
  const currentArea = computePolygonArea(shape.points);
  if (!Number.isFinite(currentArea) || currentArea <= 0) return shape;
  const ratio = targetArea / currentArea;
  if (!Number.isFinite(ratio) || ratio <= 0) return shape;
  if (Math.abs(1 - ratio) < SHAPE_SCALE_TOLERANCE) return shape;
  const scale = Math.sqrt(ratio);
  if (!Number.isFinite(scale) || scale <= 0) return shape;
  // Ratio between desired and current storey area determines the scaling factor.
  const scaledPoints = shape.points.map((point) =>
    multiplyPoint2D(point, scale),
  );
  if (!isPolygon2D(scaledPoints)) return shape;
  return {
    ...shape,
    points: scaledPoints,
  };
};

/**
 * Pick an edge to move when scaling a shape by shifting a side.
 * @param points Shape points.
 * @returns Edge start index.
 */
const getEdgeStartIndexForScaling = (
  points: ReadonlyArray<Point2D>,
): number => {
  if (points.length < 3) return 0;
  const isRectangle = points.length === 4 && isRectangleFootprint(points);
  const pickShortest = isRectangle;
  let targetLength = pickShortest ? Number.POSITIVE_INFINITY : 0;
  let edgeStartIndex = 0;

  for (let i = 0; i < points.length; i += 1) {
    const [x1, y1] = points[i] ?? DEFAULT_POINT;
    const [x2, y2] = points[(i + 1) % points.length] ?? DEFAULT_POINT;
    const length = Math.hypot(x2 - x1, y2 - y1);
    if (!Number.isFinite(length)) continue;
    // For rectangles, move the short side; otherwise move the longest edge.
    if (pickShortest) {
      if (length >= targetLength) continue;
      targetLength = length;
      edgeStartIndex = i;
    } else {
      if (length <= targetLength) continue;
      targetLength = length;
      edgeStartIndex = i;
    }
  }

  return edgeStartIndex;
};

/**
 * Check if a 4-point footprint is roughly rectangular.
 * @param points Footprint points.
 * @returns True when the footprint resembles a rectangle.
 */
const isRectangleFootprint = (points: ReadonlyArray<Point2D>): boolean => {
  if (points.length !== 4) return false;
  const p0 = points[0] ?? DEFAULT_POINT;
  const p1 = points[1] ?? DEFAULT_POINT;
  const p2 = points[2] ?? DEFAULT_POINT;
  const p3 = points[3] ?? DEFAULT_POINT;
  const v0: Point2D = [p1[0] - p0[0], p1[1] - p0[1]];
  const v1: Point2D = [p2[0] - p1[0], p2[1] - p1[1]];
  const v2: Point2D = [p3[0] - p2[0], p3[1] - p2[1]];
  const v3: Point2D = [p0[0] - p3[0], p0[1] - p3[1]];
  const len0 = Math.hypot(v0[0], v0[1]);
  const len1 = Math.hypot(v1[0], v1[1]);
  const len2 = Math.hypot(v2[0], v2[1]);
  const len3 = Math.hypot(v3[0], v3[1]);
  if (![len0, len1, len2, len3].every((len) => len > 0)) return false;

  const dot01 = Math.abs((v0[0] * v1[0] + v0[1] * v1[1]) / (len0 * len1));
  const dot12 = Math.abs((v1[0] * v2[0] + v1[1] * v2[1]) / (len1 * len2));
  // Ensure adjacent edges are near perpendicular to avoid skewed quads.
  if (dot01 > RECTANGLE_DOT_TOLERANCE || dot12 > RECTANGLE_DOT_TOLERANCE) {
    return false;
  }

  const cross02 = Math.abs(v0[0] * v2[1] - v0[1] * v2[0]) / (len0 * len2);
  const cross13 = Math.abs(v1[0] * v3[1] - v1[1] * v3[0]) / (len1 * len3);
  if (
    cross02 > RECTANGLE_PARALLEL_TOLERANCE ||
    cross13 > RECTANGLE_PARALLEL_TOLERANCE
  ) {
    return false;
  }

  const lengthMatch02 = Math.abs(len0 - len2) / Math.max(len0, len2);
  const lengthMatch13 = Math.abs(len1 - len3) / Math.max(len1, len3);
  return (
    lengthMatch02 <= RECTANGLE_LENGTH_TOLERANCE &&
    lengthMatch13 <= RECTANGLE_LENGTH_TOLERANCE
  );
};

/**
 * Scale a shape by shifting a single edge to match the target area.
 * @param shape Shape to scale.
 * @param targetArea Target area to reach.
 * @returns Scaled shape.
 */
const scaleShapeToTargetAreaByMovingEdge = (
  shape: Shape,
  targetArea: number,
): Shape => {
  if (!Number.isFinite(targetArea) || targetArea <= 0) return shape;
  const currentArea = computePolygonArea(shape.points);
  if (!Number.isFinite(currentArea) || currentArea <= 0) return shape;
  if (targetArea >= currentArea) return shape;
  const ratio = targetArea / currentArea;
  if (Math.abs(1 - ratio) < SHAPE_SCALE_TOLERANCE) return shape;
  if (shape.points.length < 3) return shape;

  const edgeStartIndex = getEdgeStartIndexForScaling(shape.points);
  const [x1, y1] = shape.points[edgeStartIndex] ?? DEFAULT_POINT;
  const [x2, y2] =
    shape.points[(edgeStartIndex + 1) % shape.points.length] ?? DEFAULT_POINT;
  const maxEdgeLength = Math.hypot(x2 - x1, y2 - y1);
  if (!Number.isFinite(maxEdgeLength) || maxEdgeLength <= 0) return shape;

  const inwardNormal = getInwardNormalForEdge(shape.points, edgeStartIndex);
  if (!inwardNormal) return shape;
  const offset = (currentArea - targetArea) / maxEdgeLength;
  if (!Number.isFinite(offset) || offset <= 0) return shape;
  const move: Point2D = [inwardNormal[0] * offset, inwardNormal[1] * offset];
  const edgeEndIndex = (edgeStartIndex + 1) % shape.points.length;

  const points = shape.points.map((point, index) => {
    if (index !== edgeStartIndex && index !== edgeEndIndex) return point;
    const shifted: Point2D = [point[0] + move[0], point[1] + move[1]];
    return shifted;
  });

  if (!isPolygon2D(points)) return shape;
  return {
    ...shape,
    points,
  };
};

/**
 * Translate legacy {@link ProjectMetadata} below-ground depth to a ground-floor elevation.
 * Keeping the sign negative keeps Boverket and SMHI oriented calculations stable.
 */
const getGroundFloorElevation = (belowGround?: number): number => {
  if (!Number.isFinite(belowGround)) return 0;
  const depth = Math.abs(belowGround ?? 0);
  if (depth === 0) return 0;
  return -depth;
};

/**
 * Inverse of {@link getGroundFloorElevation}: derive legacy below-ground depth from design elevation.
 * @param groundFloorElevation Elevation of the ground floor (negative when below grade).
 */
export const getBelowGroundFromGroundFloorElevation = (
  groundFloorElevation: number,
): number => {
  if (!Number.isFinite(groundFloorElevation) || groundFloorElevation >= 0)
    return 0;
  return Math.abs(groundFloorElevation);
};

/**
 * Derive legacy {@link ProjectMetadata} below-ground depth from design storeys.
 * Uses the first non-mezzanine storey elevation (same semantics as migration).
 * @param storeys Design storeys or calculated storey expression variables.
 */
export const getBelowGroundFromDesignStoreys = (
  storeys: ReadonlyArray<{ elevation?: number; mezzanine?: boolean }>,
): number => {
  const groundFloor = storeys.find((storey) => !storey.mezzanine) ?? storeys[0];
  return getBelowGroundFromGroundFloorElevation(groundFloor?.elevation ?? 0);
};

/**
 * Get the building location by averaging the coordinates
 * @param meta Project metadata.
 * @returns Coordinate.
 */
const getLocationFromCoordinates = (
  coordinates: OptionalReadonly<Coordinate[]>,
): Coordinate => {
  return getMean(...coordinates);
};

/**
 * Get ActivityType from deprecated activity ids/enums
 * @param activityId Activity id or enum.
 * @returns ActivityType
 */
export const toActivityType = (
  activityId: ActivityEnum | FutureActivities | ActivityId | ActivityType,
): ActivityType => {
  // Already an ActivityType, don't need to migrate
  if (activityRefGuards.is.activityType(activityId)) {
    return activityId;
  }
  switch (activityId) {
    case ActivityEnum.Stairwells:
      return ActivityType.Stairwells;
    case ActivityEnum.BuildingUtilities:
      return ActivityType.Utilities;
    case ActivityId.IndustryOffice:
      return ActivityType.Industry;
    case ActivityId.Industry:
    case ActivityId.Brewery:
    case ActivityId.ConcreteIndustry:
      return ActivityType.Industry;
    case ActivityId.Office:
    case ActivityId.Archive:
      return ActivityType.Office;
    case ActivityId.Warehouse:
      return ActivityType.Industry;
    // return ActivityType.Warehouse;
    case ActivityId.EmployeeParking:
    case ActivityId.ResidentParking:
    case ActivityId.PublicParking:
      return ActivityType.Garage;
    case ActivityId.Retail:
    case ActivityId.ShoppingCenter:
      return ActivityType.Office;
    // return ActivityType.Retail;
    case ActivityId.Restaurant:
    case ActivityId.NightClub:
      return ActivityType.Office;
    case ActivityId.Hotel:
      return ActivityType.Residential;
    case ActivityEnum.Apartments:
    case ActivityId.PrivateHousing:
    case ActivityId.Residential:
    case ActivityId.CollectiveHousing:
    case ActivityId.SpecialNeedHousing:
      return ActivityType.Residential;
    case ActivityEnum.Garage:
      return ActivityType.Garage;
    case FutureActivities.Office:
      return ActivityType.Office;
    case FutureActivities.School:
    case ActivityId.School:
    case ActivityId.GymnasticHall:
    case ActivityId.Gathering:
    case ActivityId.Library:
      return ActivityType.School;
    case ActivityEnum.Other:
      return ActivityType.Other;
    default:
      return ActivityType.Other;
  }
};

/**
 * Temporary type for activities and gfa, used to map to activities in migrations
 */
type ActivityAndGfa = { gfa: number; activity: Activity };

/**
 * Merge activities that resolve to the same {@link ActivityType} by summing GFA.
 * Keeps the first activity instance while merging properties.
 */
const mergeActivitiesByType = (
  activities: ActivityAndGfa[],
): ActivityAndGfa[] => {
  const mergedByType = new Map<ActivityType, ActivityAndGfa>();
  const orderedTypes: ActivityType[] = [];

  for (const entry of activities) {
    const existing = mergedByType.get(entry.activity.type);
    if (!existing) {
      mergedByType.set(entry.activity.type, entry);
      orderedTypes.push(entry.activity.type);
      continue;
    }

    // Merge GFA and keep migrated properties from later legacy activities.
    const mergedActivity = createActivity({
      ...existing.activity,
      properties: {
        ...existing.activity.properties,
        ...entry.activity.properties,
      },
    });
    if (guards.is.error(mergedActivity)) {
      throw mergedActivity;
    }
    mergedByType.set(entry.activity.type, {
      activity: mergedActivity,
      gfa: existing.gfa + entry.gfa,
    });
  }

  return orderedTypes
    .map((type) => mergedByType.get(type))
    .filter(guards.is.defined);
};

/**
 * Get new activities with area (used to map to activities in migrations) from project metadata.
 * Note that we both have the concept of acitivities and just setting activity_id
 * Applies 85/15 ratio for Apartments/Office/Industry vs Other/Garage/Parking when no GFA is supplied.
 * TODO: Remove when all project have been migrated to geometry 2.0
 * @param meta
 * @param gfa Provide heated area to override the default area used for the activities (=gfa)
 * @returns Space activities array.
 */
export const getActivitiesWithGfaFromMeta = (
  meta: LegacyProjectMetadata,
  gfa: number = getLegacyBuildingGFA(meta),
): ActivityAndGfa[] => {
  const legacyMeta = meta;
  const legacyResidentialOverrides =
    getLegacyResidentialPropertyOverrides(legacyMeta);
  const legacyStairwellsOverrides =
    getLegacyStairwellsPropertyOverrides(legacyMeta);
  const depecratedActivities = meta.activities?.length
    ? meta.activities.map(migrateStringGfa)
    : [createActivityFromActivityId(meta.activity_id)];

  const remainingArea = gfa - sum(depecratedActivities, 'gfa');

  const undefinedActivitiesWithTypes = depecratedActivities
    .filter((activity) => 'gfa' in activity && !guards.is.number(activity.gfa))
    .map((deprecated) => ({
      deprecated,
      type: toActivityType(
        'activityId' in deprecated ? deprecated.activityId : deprecated.type,
      ),
    }));

  const ratioEligiblePrimary = undefinedActivitiesWithTypes.filter(({ type }) =>
    RATIO_PRIMARY_ACTIVITY_TYPES.has(type),
  );
  const ratioEligibleSecondary = undefinedActivitiesWithTypes.filter(
    ({ type }) => !RATIO_PRIMARY_ACTIVITY_TYPES.has(type),
  );

  const shouldApplyRatio =
    ratioEligiblePrimary.length > 0 &&
    ratioEligibleSecondary.length > 0 &&
    ratioEligiblePrimary.length + ratioEligibleSecondary.length ===
      undefinedActivitiesWithTypes.length;

  const undefinedShareByActivity = new Map<object, number>();

  if (undefinedActivitiesWithTypes.length > 0) {
    if (shouldApplyRatio) {
      const primaryShare =
        (remainingArea * DEFAULT_APARTMENTS_RATIO) /
        ratioEligiblePrimary.length;
      const secondaryShare =
        (remainingArea * (1 - DEFAULT_APARTMENTS_RATIO)) /
        ratioEligibleSecondary.length;

      ratioEligiblePrimary.forEach(({ deprecated }) => {
        undefinedShareByActivity.set(deprecated, roundGfa(primaryShare));
      });
      ratioEligibleSecondary.forEach(({ deprecated }) => {
        undefinedShareByActivity.set(deprecated, roundGfa(secondaryShare));
      });
    } else {
      const evenShare = roundGfa(
        remainingArea / undefinedActivitiesWithTypes.length,
      );
      undefinedActivitiesWithTypes.forEach(({ deprecated }) => {
        undefinedShareByActivity.set(deprecated, evenShare);
      });
    }
  }

  const activitiesAndGfa = depecratedActivities.map((deprecated) => {
    const legacyName =
      'name' in deprecated && guards.is.string(deprecated.name)
        ? deprecated.name
        : undefined;

    const activityType = toActivityType(
      // School and offices are stored as an OtherActivity with activityId school/office
      'activityId' in deprecated ? deprecated.activityId : deprecated.type,
    );
    // Prefer legacy geometry settings when present to keep apartment counts stable.
    const legacyOverrides =
      activityType === ActivityType.Residential
        ? legacyResidentialOverrides
        : activityType === ActivityType.Stairwells
          ? legacyStairwellsOverrides
          : {};

    const activity = createActivity({
      type: activityType,
      ...(legacyName ? { name: legacyName } : {}),
      properties: {
        ...migrateDeprecatedActivityProperties(deprecated),
        ...legacyOverrides,
      },
    });

    if (guards.is.error(activity)) {
      throw activity;
    }

    const gfa =
      'gfa' in deprecated && guards.is.number(deprecated.gfa)
        ? deprecated.gfa
        : (undefinedShareByActivity.get(deprecated) ?? 0);

    return {
      gfa,
      activity,
    };
  });

  return mergeActivitiesByType(activitiesAndGfa);
};
